import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryCreator, baseQueryWithAppToken } from "redux/baseQuery";
import {
  ActivateAccountRequest,
  ChangePasswordRequest,
  CreateEnterpriseUserRequest,
  CreateEnterpriseUserResponse,
  GetUsersInvitationsRequest,
  GetUsersInvitationsResponse,
  InviteUserRequest,
  RegisterRequest,
  RegisterResponse,
  RegisterWInviteCodeRequest,
  RequestChangePasswordRequest,
  RequestNewPassword,
  RequestRecoveryAccount,
  RequestResetPassword,
  ResponseRecoveryAccount,
  SetPasswordRequest,
  BlockUnblockUserRequest,
  CheckRecoveryCodeValidationRequest,
} from "./types";
import env from "env";
import { Roles } from "common";

const { identityService } = env.api.baseUrl;

const baseQuery = fetchBaseQuery({
  baseUrl: identityService,
  prepareHeaders: (headers, { getState }: any) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const resetQuery = fetchBaseQuery({
  baseUrl: identityService,
});

export const indetityApiAppToken = createApi({
  reducerPath: "indetityApiAppToken",
  baseQuery: baseQueryWithAppToken(baseQuery),
  endpoints: (builder) => ({
    register: builder.mutation<RegisterResponse, RegisterRequest>({
      query: ({ ...form }) => {
        return {
          url: `${identityService}users`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: form,
        };
      },
    }),
    inviteUser: builder.mutation<{}, InviteUserRequest[]>({
      query: (form) => {
        return {
          url: `${identityService}users/-/invitations`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            callbackUrl: `${env.app.rootUrl}/confirm-account`,
            isOrganizationOwner: "true",
            "org-token": form[0].orgToken,
          },
          body: form,
        };
      },
    }),
    regiserAccountWithInvite: builder.mutation<{}, RegisterWInviteCodeRequest>({
      query: ({ ...form }) => {
        return {
          url: `${identityService}users`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            callbackUrl: `${env.app.rootUrl}/auth/activate`,
          },
          body: form,
        };
      },
    }),
    activateAccount: builder.mutation<{}, ActivateAccountRequest>({
      query: ({ activateToken, verificationCode }) => {
        return {
          url: `${identityService}users/verifications/${activateToken}`,
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: { verificationCode },
        };
      },
    }),
    setPassword: builder.mutation<{}, SetPasswordRequest>({
      query: ({ ...form }) => {
        return {
          url: `${identityService}users/-/passwords`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: form,
        };
      },
    }),
    checkRecoveryCodeValidation: builder.mutation<
      {},
      CheckRecoveryCodeValidationRequest
    >({
      query: ({ ...form }) => {
        return {
          url: `${identityService}users/-/passwords/recovery-code-validation`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: form,
        };
      },
    }),
    createEnterpriseUser: builder.mutation<
      CreateEnterpriseUserResponse,
      CreateEnterpriseUserRequest
    >({
      query: (form) => {
        return {
          url: `${identityService}users`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            emailAddress: form.emailAddress,
            firstName: form.firstName,
            lastName: form.lastName,
            callbackUrl: env.app.rootUrl,
            roles: [form.userRole],
          },
        };
      },
    }),
    inviteOperationUser: builder.mutation<
      CreateEnterpriseUserResponse,
      CreateEnterpriseUserRequest
    >({
      query: (form) => {
        return {
          url: `${identityService}users`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            emailAddress: form.emailAddress,
            firstName: form.firstName,
            lastName: form.lastName,
            callbackUrl: env.app.rootUrl,
            roles: [Roles.PaymentOps],
          },
        };
      },
    }),
    requestChangePassword: builder.mutation<{}, RequestChangePasswordRequest>({
      query: ({ userId }) => {
        return {
          url: `${identityService}users/${userId}/passwords/reset`,
          method: "POST",
        };
      },
    }),
    recoveryAccount: builder.mutation<
      ResponseRecoveryAccount,
      RequestRecoveryAccount
    >({
      query: ({ email }) => {
        return {
          url: `${identityService}users/-/recovery-options`,
          method: "GET",
          params: {
            identifier: email,
          },
        };
      },
    }),
    resetPassword: builder.mutation<{}, RequestResetPassword>({
      query: ({ recoveryCode }) => {
        return {
          url: `${identityService}users/passwords/reset-request`,
          method: "POST",
          body: {
            channelId: "1",
            recoveryCode: recoveryCode,
          },
        };
      },
    }),
    changePassword: builder.mutation<{}, ChangePasswordRequest>({
      query: ({ ...form }) => {
        return {
          url: `${identityService}users/-/passwords`,
          method: "PUT",
          body: form,
        };
      },
    }),
    blockUnblockUser: builder.mutation<{}, BlockUnblockUserRequest>({
      query: ({ userId, isBlock, ...body }) => ({
        url: `users/${userId}/${isBlock ? "block" : "unblock"}`,
        method: "POST",
        body: body,
      }),
    }),
  }),
});

export const indetityApi = createApi({
  reducerPath: "indetityApi",
  baseQuery: baseQueryCreator(baseQuery),
  endpoints: (builder) => ({
    getUsersInvitations: builder.query<
      GetUsersInvitationsResponse,
      GetUsersInvitationsRequest & { createdAt?: string }
    >({
      query: ({ orgToken, ...params }) => {
        return {
          url: "/users/-/invitations",
          params: {
            ...params,
            userRole: params.userRole?.join(","),
          },
          headers: {
            "org-token": orgToken,
          },
        };
      },
    }),
  }),
});

export const setPasswordApiAppToken = createApi({
  reducerPath: "setPasswordApiAppToken",
  baseQuery: baseQueryWithAppToken(resetQuery),
  endpoints: (builder) => ({
    submitNewPassword: builder.mutation<{}, RequestNewPassword>({
      query: (request) => {
        return {
          url: `${identityService}users/passwords/reset-requests/${request.verifyCode}`,
          method: "PUT",
          body: {
            password: request.password,
          },
        };
      },
    }),
  }),
});

export const {
  useRegisterMutation,
  useActivateAccountMutation,
  useInviteUserMutation,
  useRegiserAccountWithInviteMutation,
  useSetPasswordMutation,
  useCreateEnterpriseUserMutation,
  useRequestChangePasswordMutation,
  useRecoveryAccountMutation,
  useResetPasswordMutation,
  useInviteOperationUserMutation,
  useChangePasswordMutation,
  useBlockUnblockUserMutation,
  useCheckRecoveryCodeValidationMutation,
} = indetityApiAppToken;

export const { useGetUsersInvitationsQuery, useLazyGetUsersInvitationsQuery } =
  indetityApi;

export const { useSubmitNewPasswordMutation } = setPasswordApiAppToken;
